import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, Phone, User, ShieldCheck, Stethoscope,
  Clock, Calendar, CheckCircle, ChevronLeft, Loader2, Video, MapPin, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTime12h } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = (path) => `${BACKEND_URL}/api${path}`;

const STEPS = ['identity', 'otp', 'department', 'doctor', 'datetime', 'confirmed'];

function StepIndicator({ current }) {
  const labels = ['You', 'Verify', 'Dept', 'Doctor', 'Time', 'Done'];
  const idx = STEPS.indexOf(current);
  return (
    <div className="flex items-center justify-center gap-1 mb-6">
      {labels.slice(0, 5).map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < idx ? 'bg-primary text-white' :
              i === idx ? 'bg-primary text-white ring-4 ring-primary/20' :
              'bg-slate-100 text-slate-400'
            }`}>
              {i < idx ? <CheckCircle className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-xs mt-1 ${i === idx ? 'text-primary font-medium' : 'text-slate-400'}`}>
              {label}
            </span>
          </div>
          {i < 4 && <div className={`h-px w-6 mb-4 ${i < idx ? 'bg-primary' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function PublicBooking() {
  const { hospitalSlug } = useParams();

  const [hospital, setHospital] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('identity');

  // Identity + OTP
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [guestToken, setGuestToken] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Booking
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [consultationType, setConsultationType] = useState('in-person');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [meetLinkCopied, setMeetLinkCopied] = useState(false);

  useEffect(() => {
    fetchHospital();
  }, [hospitalSlug]);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const fetchHospital = async () => {
    try {
      const [hospRes, deptRes] = await Promise.all([
        axios.get(api(`/hospitals/slug/${hospitalSlug}`)),
        axios.get(api(`/public/hospitals/${hospitalSlug}/departments`)),
      ]);
      setHospital(hospRes.data);
      setDepartments(deptRes.data);
    } catch {
      toast.error('Hospital not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async (deptId) => {
    try {
      const res = await axios.get(api(`/public/hospitals/${hospitalSlug}/doctors?department_id=${deptId}`));
      setDoctors(res.data);
    } catch {
      toast.error('Failed to load doctors');
    }
  };

  const fetchSlots = async (doctorId, date) => {
    try {
      const res = await axios.get(api(`/slots/available?doctor_id=${doctorId}&date=${date}`));
      setSlots(res.data.filter(s => s.available));
    } catch {
      toast.error('Failed to load slots');
    }
  };

  // ── Step: Identity ──────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!name.trim()) return toast.error('Please enter your name');
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) return toast.error('Please enter a valid phone number');

    setOtpSending(true);
    try {
      await axios.post(api('/public/otp/send'), { phone: cleaned, name: name.trim() });
      toast.success('OTP sent to your WhatsApp!');
      setStep('otp');
      setResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  // ── Step: OTP ───────────────────────────────────────────────────────────────
  const handleOtpChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.join('');
    if (code.length < 6) return toast.error('Enter the 6-digit OTP');
    const cleaned = phone.replace(/\D/g, '');

    setOtpVerifying(true);
    try {
      const res = await axios.post(api('/public/otp/verify'), { phone: cleaned, otp: code });
      setGuestToken(res.data.token);
      toast.success(`Welcome, ${res.data.name}!`);
      setStep('department');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setOtpVerifying(false);
    }
  };

  // ── Step: Department ────────────────────────────────────────────────────────
  const handleDeptSelect = async (dept) => {
    setSelectedDept(dept);
    await fetchDoctors(dept.id);
    setStep('doctor');
  };

  // ── Step: Doctor ────────────────────────────────────────────────────────────
  const handleDoctorSelect = (doctor) => {
    setSelectedDoctor(doctor);
    setStep('datetime');
  };

  // ── Step: Date & Time ───────────────────────────────────────────────────────
  const handleDateChange = (date) => {
    setAppointmentDate(date);
    setAppointmentTime('');
    if (selectedDoctor) fetchSlots(selectedDoctor.id, date);
  };

  const handleConfirm = async () => {
    if (!appointmentTime) return toast.error('Please select a time slot');
    setBooking(true);
    try {
      const res = await axios.post(api('/public/book'), {
        guest_token: guestToken,
        hospital_id: hospital.id,
        department_id: selectedDept.id,
        doctor_id: selectedDoctor?.id,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        consultation_type: consultationType,
      });
      setConfirmed(res.data);
      setStep('confirmed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const copyMeetLink = async () => {
    try {
      await navigator.clipboard.writeText(confirmed.google_meet_link);
      setMeetLinkCopied(true);
      toast.success('Meet link copied');
      setTimeout(() => setMeetLinkCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link. Please copy it manually.');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-sm w-full mx-4">
          <CardHeader>
            <CardTitle>Not Found</CardTitle>
            <CardDescription>This hospital booking page does not exist.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">

        {/* Hospital Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-3">
            <Building2 className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-xl font-manrope font-bold text-slate-900">{hospital.name}</h1>
          {hospital.address && (
            <p className="text-sm text-slate-500 flex items-center justify-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />{hospital.address}
            </p>
          )}
        </div>

        {/* Step Indicator */}
        {step !== 'confirmed' && <StepIndicator current={step} />}

        {/* ── Step: Identity ── */}
        {step === 'identity' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-primary" /> Book an Appointment
              </CardTitle>
              <CardDescription>Enter your details to get started</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="e.g. Rahul Sharma"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && document.getElementById('phone')?.focus()}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">WhatsApp Number</Label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-slate-100 border rounded-md text-sm text-slate-600 whitespace-nowrap">
                    🇮🇳 +91
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="98765 43210"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                    maxLength={15}
                  />
                </div>
                <p className="text-xs text-slate-500">We'll send a verification code on WhatsApp</p>
              </div>
              <Button className="w-full" onClick={handleSendOtp} disabled={otpSending}>
                {otpSending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending OTP...</> : <>
                  <Phone className="h-4 w-4 mr-2" /> Send OTP on WhatsApp
                </>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Step: OTP ── */}
        {step === 'otp' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="h-5 w-5 text-primary" /> Verify Your Number
              </CardTitle>
              <CardDescription>
                Enter the 6-digit OTP sent to <strong>+91 {phone}</strong> on WhatsApp
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-2 justify-center">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(e.target.value, i)}
                    onKeyDown={e => handleOtpKeyDown(e, i)}
                    className="w-11 h-12 text-center text-xl font-bold border-2 rounded-lg focus:border-primary focus:outline-none transition-colors"
                  />
                ))}
              </div>
              <Button className="w-full" onClick={handleVerifyOtp} disabled={otpVerifying}>
                {otpVerifying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</> : 'Verify & Continue'}
              </Button>
              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-sm text-slate-500">Resend OTP in {resendTimer}s</p>
                ) : (
                  <button
                    className="text-sm text-primary hover:underline"
                    onClick={() => { setOtp(['','','','','','']); setStep('identity'); }}
                  >
                    Didn't receive? Resend OTP
                  </button>
                )}
              </div>
              <button className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" onClick={() => setStep('identity')}>
                <ChevronLeft className="h-4 w-4" /> Change number
              </button>
            </CardContent>
          </Card>
        )}

        {/* ── Step: Department ── */}
        {step === 'department' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="h-5 w-5 text-primary" /> Select Department
              </CardTitle>
              <CardDescription>Hi {name}, choose the department for your appointment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  className="w-full text-left p-4 border-2 rounded-lg hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => handleDeptSelect(dept)}
                >
                  <p className="font-semibold text-slate-900">{dept.name}</p>
                  {dept.description && <p className="text-sm text-slate-500 mt-0.5">{dept.description}</p>}
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ── Step: Doctor ── */}
        {step === 'doctor' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setStep('department')} className="text-slate-400 hover:text-slate-600">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <CardTitle className="text-lg">{selectedDept?.name}</CardTitle>
              </div>
              <CardDescription>Choose consultation type and doctor</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Consultation Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'in-person', label: 'In-Person', icon: MapPin },
                    { value: 'video', label: 'Video Call', icon: Video },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setConsultationType(value)}
                      className={`flex items-center gap-2 p-3 border-2 rounded-lg transition-all ${
                        consultationType === value ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Doctor</Label>
                {doctors.length === 0 ? (
                  <Alert><AlertDescription>No doctors available in this department.</AlertDescription></Alert>
                ) : doctors.map((doc) => (
                  <button
                    key={doc.id}
                    className="w-full text-left p-4 border-2 rounded-lg hover:border-primary hover:bg-primary/5 transition-all"
                    onClick={() => handleDoctorSelect(doc)}
                  >
                    <p className="font-semibold text-slate-900">
                      {doc.full_name || (doc.user_id?.includes('@') ? doc.user_id?.split('@')[0] : doc.user_id)}
                    </p>
                    <p className="text-sm text-slate-500">{doc.specialization}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Step: Date & Time ── */}
        {step === 'datetime' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setStep('doctor')} className="text-slate-400 hover:text-slate-600">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <CardTitle className="text-lg">Pick a Time</CardTitle>
              </div>
              <CardDescription>
                {selectedDept?.name} · {consultationType === 'video' ? 'Video Call' : 'In-Person'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="appt-date" className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" /> Date
                </Label>
                <Input
                  id="appt-date"
                  type="date"
                  min={today}
                  value={appointmentDate}
                  onChange={e => handleDateChange(e.target.value)}
                />
              </div>

              {appointmentDate && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock className="h-4 w-4" /> Available Slots
                  </Label>
                  {slots.length === 0 ? (
                    <Alert><AlertDescription>No slots available for this date. Try another day.</AlertDescription></Alert>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setAppointmentTime(slot.time)}
                          className={`py-2 px-3 text-sm rounded-lg border-2 transition-all font-medium ${
                            appointmentTime === slot.time
                              ? 'bg-primary text-white border-primary'
                              : 'border-slate-200 text-slate-700 hover:border-primary hover:bg-primary/5'
                          }`}
                        >
                          {formatTime12h(slot.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {appointmentDate && appointmentTime && (
                <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-1">
                  <p className="font-medium text-slate-700">Booking Summary</p>
                  <p className="text-slate-500"><span className="font-medium">Patient:</span> {name}</p>
                  <p className="text-slate-500"><span className="font-medium">Date:</span> {new Date(appointmentDate + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  <p className="text-slate-500"><span className="font-medium">Time:</span> {appointmentTime}</p>
                  <p className="text-slate-500"><span className="font-medium">Type:</span> {consultationType === 'video' ? 'Video Consultation' : 'In-Person Visit'}</p>
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleConfirm}
                disabled={!appointmentTime || booking}
              >
                {booking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming...</> : 'Confirm Appointment'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Step: Confirmed ── */}
        {step === 'confirmed' && confirmed && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-manrope font-bold text-green-900">Appointment Confirmed!</h2>
                <p className="text-green-700 text-sm mt-1">See you at {hospital.name}</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-sm space-y-2 text-left border border-green-100">
                <div className="flex justify-between">
                  <span className="text-slate-500">Patient</span>
                  <span className="font-medium">{confirmed.patient_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="font-medium">{new Date(confirmed.appointment_date + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Time</span>
                  <span className="font-medium">{formatTime12h(confirmed.appointment_time)}</span>
                </div>
                {confirmed.google_meet_link && (
                  <div className="pt-2 border-t border-green-100 space-y-2">
                    <div className="flex items-center gap-2">
                      <Video className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="flex-1 truncate text-slate-600 text-xs">{confirmed.google_meet_link}</span>
                      <button
                        onClick={copyMeetLink}
                        className="flex items-center gap-1.5 py-1.5 px-3 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors flex-shrink-0"
                      >
                        {meetLinkCopied ? (
                          <><Check className="h-3.5 w-3.5" /> Copied</>
                        ) : (
                          <><Copy className="h-3.5 w-3.5" /> Copy Link</>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-green-700">
                      Save this link and join at {formatTime12h(confirmed.appointment_time)}.
                    </p>
                  </div>
                )}
              </div>
              {!confirmed.google_meet_link && (
                <p className="text-xs text-green-700">
                  Please arrive 10 minutes before your appointment.
                </p>
              )}
              <button
                className="text-sm text-green-700 underline"
                onClick={() => {
                  setStep('identity');
                  setName(''); setPhone(''); setOtp(['','','','','','']);
                  setGuestToken(null); setSelectedDept(null); setSelectedDoctor(null);
                  setAppointmentDate(''); setAppointmentTime(''); setConfirmed(null);
                }}
              >
                Book another appointment
              </button>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}
